import React from "react";
import { FormGroup, Label, Input } from "reactstrap";

/**
 * Shared semantic + NL query controls for Drupal admin.
 */
export default function SemanticSearchSettings({
	semanticSearchEnabled,
	onSemanticSearchEnabledChange,
	semanticSourceFieldsText,
	onSemanticSourceFieldsTextChange,
	semanticHybridAlpha,
	onSemanticHybridAlphaChange,
	nlQueryEnabled,
	onNlQueryEnabledChange,
	nlModelId,
	onNlModelIdChange,
	keywordMatchMode,
	onKeywordMatchModeChange,
}) {
	return (
		<div className="mt-4 pt-3 border-top">
			<h3 className="h6">Search options</h3>
			<p className="text-muted small">
				Enabling hybrid semantic search requires a full product re-index. Natural language query must be
				configured on your search backend before you enable it below.
			</p>

			<FormGroup>
				<Label>Keyword match mode</Label>
				<Input
					type="select"
					value={keywordMatchMode === "loose" ? "loose" : "strict"}
					onChange={(e) => onKeywordMatchModeChange(e.target.value === "loose" ? "loose" : "strict")}
				>
					<option value="strict">Strict — all query terms must match</option>
					<option value="loose">Loose — allow Typesense to drop tokens for broader recall</option>
				</Input>
				<small className="text-muted">
					Applies to autocomplete, instant search, and search results. SKU and title-no-space legs stay strict.
				</small>
			</FormGroup>

			<FormGroup check className="mb-2 mt-3">
				<Label check>
					<Input
						type="checkbox"
						checked={Boolean(semanticSearchEnabled)}
						onChange={(e) => onSemanticSearchEnabledChange(e.target.checked)}
					/>{" "}
					Enable hybrid semantic search
				</Label>
			</FormGroup>

			{semanticSearchEnabled && (
				<>
					<FormGroup>
						<Label>Text fields for semantic search</Label>
						<Input
							type="text"
							value={semanticSourceFieldsText ?? ""}
							onChange={(e) => onSemanticSourceFieldsTextChange(e.target.value)}
							placeholder="title, product_title, field_description"
						/>
						<small className="text-muted">Separate field names with commas (e.g. title, product_title, field_description).</small>
					</FormGroup>
					<FormGroup>
						<Label>Hybrid alpha (semantic vs keyword balance, 0–1)</Label>
						<Input
							type="number"
							min={0}
							max={1}
							step={0.05}
							value={semanticHybridAlpha ?? 0.3}
							onChange={(e) => onSemanticHybridAlphaChange(parseFloat(e.target.value))}
						/>
						<small className="text-muted">
							Higher alpha = more semantic/vector weight; default 0.3 (baseline for 2-token queries)
						</small>
					</FormGroup>
				</>
			)}

			<FormGroup check className="mb-2 mt-3">
				<Label check>
					<Input
						type="checkbox"
						checked={Boolean(nlQueryEnabled)}
						onChange={(e) => onNlQueryEnabledChange(e.target.checked)}
					/>{" "}
					Enable Natural Language Query (NL)
				</Label>
			</FormGroup>

			{nlQueryEnabled && (
				<FormGroup>
					<Label>Natural language model ID</Label>
					<Input
						value={nlModelId || ""}
						onChange={(e) => onNlModelIdChange(e.target.value)}
						placeholder="e.g. nl-model-products"
					/>
					<small className="text-muted">Use the model ID configured for your product search index.</small>
				</FormGroup>
			)}
		</div>
	);
}

/** Parse comma-separated field names for API save. */
export function parseSemanticSourceFieldsText(text) {
	return String(text ?? "")
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
}

/** Format stored field array for the text input. */
export function formatSemanticSourceFieldsText(fields) {
	if (!Array.isArray(fields) || fields.length === 0) return "title, product_title, field_description";
	return fields.join(", ");
}
